/**
 * Vasaloppet Mina Sidor
 * Author: Peter Löfås, peter@lofas.se
 */

import React from "react"
import FirebaseContext from "../../../providers/Firebase/context";
import { Card, CardHeader, CardContent, Theme, withStyles, Button, createStyles, Grid, TableContainer, Paper, Table, TableHead, TableRow, TableBody, TableCell, Dialog, DialogTitle, DialogActions, LinearProgress, DialogContent, TextField, Typography, Select, MenuItem } from "@material-ui/core";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { ApiBackend } from "../../../providers/apibackend";
import { Product } from "../../../model/Product";
import { Event } from "../../../model/Event";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { cloneDeep } from "lodash";
import uuid from "react-uuid";
import EventRow from "./EventRow";
import ProductFilter from "./ProductFilter";
import { vasaloppetDateTimeGetterFormatter } from "../../../utilities/date";

interface State {
    loading: boolean;
    products: Product[],
    events: Event[];
    deleteConfirmation: Product;
    showDeleteConfirmation: boolean;
    showArchived: boolean;
    duplicateItem: Product;
    duplicateItemName: string;
    duplicateItemNameEn: string;
    duplicateItemEvent: string;
    productSearchFilter: string;
}

class ManageProducts extends React.Component<RouteComponentProps, State> {
    static contextType = FirebaseContext;

    constructor(props: RouteComponentProps) {
        super(props);
        this.state = {
            loading: true,
            products: null,
            events: null,
            deleteConfirmation: null,
            showDeleteConfirmation: false,
            showArchived: false,
            duplicateItem: null,
            duplicateItemEvent: null as string,
            duplicateItemName: null as string,
            duplicateItemNameEn: null as string,
            productSearchFilter: ""
        };
    }

    componentDidMount() {
        this.setState({ loading: true });
        const back = new ApiBackend();

        const work: (Promise<Product[]> | Promise<Event[]>)[] = [
            back.listProducts(),
            back.listEvents()
        ];

        Promise.all(work).then(res => {
            this.setState({ products: res[0] as Product[], events: res[1] as Event[], loading: false });
        });
    }

    private handleAddProduct = () => {
        this.props.history.push("/products/create");
    };

    private handleEditProduct = (product: Product) => {
        this.props.history.push("/products/manage/" + product.id);
    };

    private handleDuplicateProduct = (product: Product) => {
        this.setState({ duplicateItem: product, duplicateItemName: "Kopia av " + product.name, duplicateItemNameEn: "Copy of " + product.name_en, duplicateItemEvent: product.EventId });
    };

    private handleDeleteProduct = (product: Product) => {
        this.setState({ deleteConfirmation: product, showDeleteConfirmation: true });
    };

    private updateProductList = () => {
        this.setState({ loading: true });
        let back = new ApiBackend();
        back.listProducts(this.state.showArchived).then((products) => {
            this.setState({ products: products, loading: false });
        });
    };

    private renderProductsPerEventGroup() {
        if (!this.state || !this.state.products || !this.state.events) {
            return null;
        }

        const tableRows = [];
        const eventGroups: Event[] = [];
        const eventProductsMap = new Map<string, Product[]>();

        eventGroups.push({ id: "", name: "Övriga" } as Event);
        eventProductsMap.set("", []);

        for (let i = 0; i < this.state.products.length; i++) {
            const product = this.state.products[i];
            let evId = product.EventId;
            if (evId == null) {
                evId = "";
            }

            if (!eventProductsMap.has(evId)) {
                const event = this.state.events.find(x => x.id === evId);
                if (!!event) {
                    eventGroups.push(event);
                    eventProductsMap.set(evId, [product]);
                } else {
                    product.EventId = "";
                    const cur = eventProductsMap.get("");
                    cur.push(product);
                    eventProductsMap.set("", cur);
                }
            } else {
                const cur = eventProductsMap.get(evId);
                cur.push(product);
                eventProductsMap.set(evId, cur);
            }
        }

        const eventFilterPredicate = (a: Event, b: Event): number => {
            if (!a.startDate && !b.startDate) {
                return 0;
            }

            if (!a.startDate) {
                return 1;
            }

            if (!b.startDate) {
                return -1;
            }

            return vasaloppetDateTimeGetterFormatter(a.startDate) < vasaloppetDateTimeGetterFormatter(b.startDate) ? 1 : -1;
        };

        eventGroups.sort(eventFilterPredicate);
        for (let i = 0; i < eventGroups.length; i++) {
            const event = eventGroups[i];
            const productsInEvent = eventProductsMap.get(eventGroups[i].id);

            const eventRow = <EventRow
                key={i}
                id={i}
                event={event}
                products={productsInEvent}
                productFilter={this.state.productSearchFilter}
                onRequestDuplicateProduct={this.handleDuplicateProduct}
                onRequestEditProduct={this.handleEditProduct}
                onRequestDeleteProduct={this.handleDeleteProduct}
            />

            tableRows.push(eventRow)
        }

        return tableRows;
    }

    render() {
        const { classes } = this.props as any;

        return (
            <>
                <Grid container className={classes.root} spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader className={classes.cardHeader} title="Produkter" />
                            <CardContent>
                                {this.state.loading &&
                                    <LinearProgress color="secondary" />
                                }

                                <Grid container className={classes.root} spacing={2}>
                                    <Grid item xs={3}>
                                        <Button
                                            variant="contained"
                                            onClick={this.handleAddProduct}>
                                            Lägg till produkt
                                        </Button>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <ProductFilter
                                            initialValue={this.state.productSearchFilter}
                                            onChange={(nextValue) => {
                                                this.setState({ productSearchFilter: nextValue });
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={5}>
                                        <FormControlLabel
                                            label="Inkludera arkiverade produkter"
                                            control={
                                                <Checkbox
                                                    value={this.state.showArchived}
                                                    onChange={(ev) => {
                                                        this.setState({ showArchived: ev.target.checked }, () => {
                                                            this.updateProductList();
                                                        });
                                                    }}
                                                />
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TableContainer component={Paper}>
                                            <Table size="small" >
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell>Namn</StyledTableCell>
                                                        <StyledTableCell>Evenemang</StyledTableCell>
                                                        <StyledTableCell>Antal varianter</StyledTableCell>
                                                        <StyledTableCell></StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {this.renderProductsPerEventGroup()}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                {this.state && this.state.showDeleteConfirmation && this.renderConfirmationDialog()}
                {this.state && this.state.duplicateItem && this.renderDuplicateDialog()}
            </>
        );
    }

    private renderConfirmationDialog() {
        return <Dialog
            disableEscapeKeyDown
            maxWidth="xs"
            aria-labelledby="confirmation-dialog-title"
            open={true}
        >
            <DialogTitle id="confirmation-dialog-title">Är du säker på att du vill ta bort produkten?</DialogTitle>
            <DialogActions>
                <Button variant="contained" autoFocus onClick={this.handleDeleteCancel} color="primary">
                    Avbryt
                </Button>
                <Button variant="contained" onClick={this.handleDeleteOk} color="primary">
                    Ja, ta bort
                </Button>
            </DialogActions>
        </Dialog>;
    }

    private renderDuplicateDialog() {
        return <Dialog
            disableEscapeKeyDown
            maxWidth="xs"
            aria-labelledby="confirmation-dialog-title"
            open={true}
        >
            <DialogTitle id="confirmation-dialog-title">Skapa kopia av produkten</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="body2">Nytt namn</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField value={this.state.duplicateItemName} onChange={(ev) => { this.setState({ duplicateItemName: ev.target.value }); }} fullWidth />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2">Nytt namn (engelska)</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField value={this.state.duplicateItemNameEn} onChange={(ev) => { this.setState({ duplicateItemNameEn: ev.target.value }); }} fullWidth />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2">Placera i evenemang</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Select value={this.state.duplicateItemEvent} onChange={(ev) => {
                            this.setState(
                                {
                                    duplicateItemEvent: ev.target.value as string
                                });
                        }}
                            fullWidth>
                            {this.state.events.map((x) => {
                                return <MenuItem value={x.id}>{x.name}</MenuItem>
                            })}
                        </Select>
                    </Grid>

                </Grid>

            </DialogContent>
            <DialogActions>
                <Button variant="contained" autoFocus onClick={this.handleDuplicateCancel} color="primary">
                    Avbryt
                </Button>
                <Button variant="contained" onClick={this.handleDuplicateOk} color="primary">
                    Ja, skapa kopia
                </Button>
            </DialogActions>
        </Dialog>;
    }

    private handleDeleteCancel = () => {
        this.setState({ deleteConfirmation: null, showDeleteConfirmation: false });
    };

    private handleDuplicateCancel = () => {
        this.setState({ duplicateItem: null });
    };

    private handleDeleteOk = () => {
        this.setState({ loading: true });
        var back = new ApiBackend();
        back.deleteProduct(this.state.deleteConfirmation).then((res) => {
            if (res) {
                back.listProducts().then((products) => {
                    this.setState({ deleteConfirmation: null, showDeleteConfirmation: false, products: products, loading: false });
                });
            }
            else {
                alert("Kunde inte ta bort product!");
            }
        })
    };

    private handleDuplicateOk = () => {
        this.setState({ loading: true });
        var back = new ApiBackend();
        let newProduct = new Product();

        newProduct = cloneDeep(this.state.duplicateItem);

        newProduct.EventId = this.state.duplicateItemEvent;
        newProduct.name = this.state.duplicateItemName;
        newProduct.name_en = this.state.duplicateItemNameEn;
        newProduct.IsArchived = false;
        newProduct.id = uuid();
        // Eget publikt id sätts av admin på kopian - undvik dubblett med källprodukten
        newProduct.publicId = "";

        // Karta gammalt -> nytt variant-id för att kunna peka om startgruppernas participantCategory
        const variantIdMap = new Map<string, string>();
        if (newProduct.variants) {
            for (let i = 0; i < newProduct.variants.length; i++) {
                const oldVariantId = newProduct.variants[i].Id;
                newProduct.variants[i].Id = uuid();
                variantIdMap.set(oldVariantId, newProduct.variants[i].Id);
                if (newProduct.variants[i].priceGroups) {
                    for (let pg = 0; pg < newProduct.variants[i].priceGroups.length; pg++) {
                        newProduct.variants[i].priceGroups[pg].id = uuid();
                    }
                }
                if (newProduct.variants[i].occations) {
                    for (let o = 0; o < newProduct.variants[i].occations.length; o++) {
                        newProduct.variants[i].occations[o].id = uuid();
                    }
                }
            }
        }

        // Startgrupper har egna primärnyckel-id:n som MÅSTE regenereras, annars krockar de med
        // källproduktens startgrupper (duplicate key -> 502 vid skapande). participantCategory pekar
        // dessutom på ett variant-id som just bytts ut och måste pekas om.
        if (newProduct.startGroups) {
            for (let i = 0; i < newProduct.startGroups.length; i++) {
                newProduct.startGroups[i].id = uuid();
                const cat = newProduct.startGroups[i].participantCategory;
                if (cat && variantIdMap.has(cat)) {
                    newProduct.startGroups[i].participantCategory = variantIdMap.get(cat);
                }
            }
        }

        back.createProduct(newProduct).then((res) => {
            this.setState({ loading: false, duplicateItem: null });
            if (!res) {
                alert("Kunde inte skapa kopia av produkten. Försök igen eller kontakta support.");
                return;
            }
            this.props.history.push("/products/manage/" + newProduct.id);
        });
    };
}

const useStyles = ({ palette, spacing }: Theme) => createStyles({
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    },
    photo: {
        height: "30px",
        verticalAlign: "middle",
        borderRadius: "10px"
    },
    root: {

    },
    form: {
        "& > *": {
            margin: spacing(1),
            width: "25ch",
        },
        "& label.Mui-focused": {
            color: palette.secondary.main,
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: palette.secondary.main,
        },
    }
}
);

const tableHeadStyles = ({ palette, spacing }: Theme) => createStyles({
    head: {
        background: palette.primary.main,
        color: palette.primary.contrastText,
    }
});

const StyledTableCell = withStyles(tableHeadStyles)(TableCell);

export default withRouter(withStyles(useStyles)(ManageProducts));
